import React, { useState, useRef, useEffect } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { useLayoutContext } from '../../layouts/general-layout';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { Loader } from '../../assets/images/index';
import ScrollableBookLayout from './ScrollableBookLayout';
import './BookViewer.scss';

interface BookViewerProps {
  bookContent: any;
  isLoading?: boolean;
  isGenerating?: boolean;
  showHeader?: boolean;
  className?: string;
  closeButton?: React.ReactNode;
}

const BookViewer: React.FC<BookViewerProps> = ({ 
  bookContent, 
  isLoading = false, 
  isGenerating = false,
  showHeader = true,
  className = "",
  closeButton
}) => {
  const { t } = useLayoutContext();
  const { shouldUseScrollLayout } = useResponsiveLayout();
  const [currentPage, setCurrentPage] = useState(0);
  const flipBookRef = useRef<any>(null);

  const updateRealViewportHeight = () => {
    const realHeight = window.innerHeight + 'px'; 

    document.documentElement.style.setProperty('--real-viewport-height', realHeight);
  };

  useEffect(() => {
    window.addEventListener('resize', updateRealViewportHeight);
    window.addEventListener('load', updateRealViewportHeight);
    
    updateRealViewportHeight();

    return () => {
      window.removeEventListener('resize', updateRealViewportHeight);
      window.removeEventListener('load', updateRealViewportHeight);
    };
  }, []);


  const handlePreviousPage = () => {
    if (currentPage > 0) {
      const newPage = Math.max(0, currentPage - 2);
      setCurrentPage(newPage);
      if (flipBookRef.current) {
        flipBookRef.current.pageFlip().flip(newPage);
      }
    }
  };

    const handleNextPage = () => {
      if (bookContent?.pages) {
        const totalPages = 1 + 2 + (bookContent.pages.length - 2) * 2;
        
        if (currentPage < totalPages - 1) {
          const newPage = Math.min(totalPages - 1, currentPage + 2);
          setCurrentPage(newPage);
          if (flipBookRef.current) {
            flipBookRef.current.pageFlip().flip(newPage);
          }
        }
      }
    };
    const getRenderedPagePosition = (targetPageKey: string) => {
      let renderedPagePosition = 0;
      
      for (let i = 0; i < bookContent.pages.length; i++) {
        const page = bookContent.pages[i];
        const pageKey = Object.keys(page)[0];
        
        if (pageKey === targetPageKey) {
          return renderedPagePosition;
        }
        
        if (pageKey === 'page_1') {
          renderedPagePosition += 1;
        } else if (pageKey === 'page_2') {
          renderedPagePosition += 2;
        } else {
          renderedPagePosition += 2; // All content pages are 2-page spreads
        }
      }
      
      return 0;
    };


  const handleCtaNavigation = (option: 'A' | 'B', currentPageKey: string) => {
    const basePageNumber = parseInt(currentPageKey.replace('page_', ''));
    const nextPageNumber = basePageNumber + 1;
    const targetPageKey = `page_${nextPageNumber}${option}`;
    
    const position = getRenderedPagePosition(targetPageKey);
    
    setCurrentPage(position);
    if (flipBookRef.current) {
      setTimeout(() => {
        flipBookRef.current.pageFlip().flip(position);
      }, 100);
    }
  };

  const getBookTitle = () => {
    if (bookContent && bookContent.pages && bookContent.pages.length > 0) {
      const firstPage = bookContent.pages[0];
      const firstPageKey = Object.keys(firstPage)[0];
      const pageContent = firstPage[firstPageKey];
      return pageContent?.title || t('noTitleFound');
    }
    return '';
  };

  return (
    <div className={`book-viewer ${className}`}>
      {(showHeader || closeButton) && (
        <div className="book-content-header">
          {closeButton}
          <h3>
            {t('bookName')} {(isGenerating || isLoading) ? (
              <span className="book-title-loader">
                <img src={Loader} alt={t('loading')} className="book-title-loader-img" />
              </span>
            ) : (
              getBookTitle()
            )}
          </h3>
        </div>
      )}

      {(isLoading || isGenerating) ? (
        <div className="book-viewer-loading">
          <div>
            <img src={Loader} alt={t('loading')} />
            <p className="loading-text">{t('loadingBookContent')}</p>
          </div>
        </div>
      ) : !bookContent ? (
        <div className="book-viewer-error">
          <p>{t('noTitleFound')}</p>
        </div>
      ) : (
        <div className={`book-content-container ${shouldUseScrollLayout ? 'scroll-layout' : ''}`}>
          {shouldUseScrollLayout ? (
            <ScrollableBookLayout bookContent={bookContent} />
          ) : (
            <>
              <div className="flipbook-wrapper">
                <HTMLFlipBook
                  width={500}
                  height={600}
                  size="fixed"
                  minWidth={315}
                  maxWidth={1024}
                  minHeight={400}
                  maxHeight={1024}
                  showCover={true}
                  flippingTime={1000}
                  className="book-container book-container-styled"
                  startPage={currentPage}
                  drawShadow={true}
                  usePortrait={false}
                  startZIndex={0}
                  autoSize={true}
                  maxShadowOpacity={0.5}
                  clickEventForward={true}
                  useMouseEvents={true}
                  swipeDistance={30}
                  showPageCorners={true}
                  disableFlipByClick={false}
                  mobileScrollSupport={true}
                  onFlip={(e: any) => setCurrentPage(e.data)}
                  style={{}}
                  ref={flipBookRef}
                >
                {(() => {
                    const allPages: React.ReactElement[] = [];
                    
                    bookContent.pages && bookContent.pages.forEach((page: any, index: number) => {
                      const pageKey = Object.keys(page)[0];
                      const pageContent = page[pageKey];
                      const hasImage = pageContent?.image_url?.trim();
                      
                      if (pageKey === 'page_1') {
                        // Front cover
                        const coverUrl = pageContent?.front_cover_image_url?.trim();
                        allPages.push(
                          <div key={index} className={`page ${coverUrl ? 'title-page-cover' : ''}`}>
                            {coverUrl && <img src={coverUrl} alt="Front cover" className="cover-image" />}
                            <div className="title-page-content-wrapper">
                              <h2>{pageContent.title}</h2>
                              <p>{t('by')} {pageContent.author}</p>
                            </div>
                          </div>
                        );
                      } else if (pageKey === 'page_2') {
                        // Blank + Dedication
                        allPages.push(
                          <div key={`${index}-blank`} className="page"><div></div></div>,
                          <div key={`${index}-dedication`} className="page">
                            <div>
                              <h3>{t('dedicationLabel')}</h3>
                              <p>{pageContent?.content || t('none')}</p>
                            </div>
                          </div>
                        );
                      } else {
                        // Content pages: always 2-page spread (content + image/blank)
                        allPages.push(
                          <div key={`${index}-content`} className="page">
                            <div>
                              <p>{pageContent.content}</p>
                              {pageContent.cta_option_a && pageContent.cta_option_b && (
                                <div className="cta-buttons">
                                  <button className="cta-btn cta-option-a" onClick={() => handleCtaNavigation('A', pageKey)}>
                                    {pageContent.cta_option_a}
                                  </button>
                                  <button className="cta-btn cta-option-b" onClick={() => handleCtaNavigation('B', pageKey)}>
                                    {pageContent.cta_option_b}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>,
                          hasImage ? (
                            <div key={`${index}-image`} className="page image-page">
                              <div className="image-container">
                                <img src={hasImage} alt="Story illustration" className="story-image" />
                              </div>
                            </div>
                          ) : (
                            <div key={`${index}-blank`} className="page"><div></div></div>
                          )
                        );
                      }
                    });
                    
                    return allPages;
                  })()}

                </HTMLFlipBook>
              </div>

              <div className="navigation-controls">
                <button
                  className={`nav-btn prev-btn ${currentPage === 0 ? 'disabled' : ''}`}
                  onClick={handlePreviousPage}
                  disabled={currentPage === 0}
                >
                  {t('previousPage')}
                </button>

                <span className="page-counter">
                  {bookContent.pages ? (() => {
                    const contentPages = bookContent.pages.filter((p: any) => {
                      const key = Object.keys(p)[0];
                      return key !== 'page_1' && key !== 'page_2';
                    }).length;
                    
                    if (currentPage <= 2) return `[ Page - ${t('of')} ${contentPages} ]`;
                    
                    const currentContentPage = Math.min(Math.floor((currentPage - 2) / 2), contentPages);
                    return `[ Page ${currentContentPage} ${t('of')} ${contentPages} ]`;
                  })() : `- ${t('of')} 0`}
                </span>

                <button
                  className={`nav-btn next-btn ${(() => {
                    if (!bookContent?.pages) return 'disabled';
                    const totalPages = 1 + 2 + (bookContent.pages.length - 2) * 2; // cover + dedication + content spreads
                    return currentPage >= totalPages - 2 ? 'disabled' : '';
                  })()}`}
                  onClick={handleNextPage}
                  disabled={(() => {
                    if (!bookContent?.pages) return true;
                    const totalPages = 1 + 2 + (bookContent.pages.length - 2) * 2;
                    return currentPage >= totalPages - 2;
                  })()}
                >
                  {t('nextPage')}
                </button>

              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BookViewer;
